import { useLoaderData, useRouteLoaderData } from "react-router-dom";
import { Organization } from "../../../services/organization/organizationModel";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { ReturnButton } from "../../../components/ReturnButton";
import {
  PREMIUM_SPONSORSHIP_STATUS_LABEL_KEYS,
  PREMIUM_SPONSORSHIP_STATUSES,
  PremiumSponsorship,
  PremiumSponsorshipStatus,
} from "../../../services/premiumSponsorship/premiumSponsorshipModel";
import InvitePremiumSponsorshipsDialog from "./InvitePremiumSponsorshipsDialog";
import PremiumSponsorshipsTable from "./PremiumSponsorshipsTable";
import PremiumSponsorshipsOverview from "./PremiumSponsorshipsOverview";
import { useSearch, useTableSort } from "../../../react-helpers/array";

const PremiumSponsorshipPage = () => {
  const { t } = useTranslation(["organization"]);
  const { organization } = useRouteLoaderData("organization") as {
    organization: Organization;
  };
  const { sponsorships } = useLoaderData() as {
    sponsorships: PremiumSponsorship[];
  };
  const [isInviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [searchedSponsorships, setSearch] = useSearch(
    sponsorships,
    (sponsorship) => sponsorship.email,
  );

  const [statusFilter, setStatusFilter] = useState<
    PremiumSponsorshipStatus | "all"
  >("all");

  const filteredSponsorships = useMemo(
    () =>
      statusFilter === "all"
        ? searchedSponsorships
        : searchedSponsorships.filter(
            (sponsorship) => sponsorship.status === statusFilter,
          ),
    [searchedSponsorships, statusFilter],
  );

  const comparators = useMemo(
    () => ({
      // Sorted on the lifecycle order rather than on the translated label.
      status: (a: PremiumSponsorship, b: PremiumSponsorship) =>
        PREMIUM_SPONSORSHIP_STATUSES.indexOf(a.status) -
        PREMIUM_SPONSORSHIP_STATUSES.indexOf(b.status),
    }),
    [],
  );

  const [sortedSponsorships, sort, toggleSort] = useTableSort(
    filteredSponsorships,
    comparators,
  );

  return (
    <div className="layout_content --bg--organization-dashboard">
      <div className="page-content container --wrap">
        <ReturnButton fallbackUrl="../.." />
        <h1 className="page_title">
          {t("organization:premium-sponsorship.PAGE_TITLE")}
        </h1>
        <p className="lblock--l">
          {t("organization:premium-sponsorship.SUBTITLE")}
        </p>

        <div className="lblock lrow --stretch--v">
          <PremiumSponsorshipsOverview
            organization={organization}
            sponsorships={sponsorships}
            onInvite={() => setInviteDialogOpen(true)}
          />
        </div>

        <div className="lblock">
          <div className="lblock--l grid-3">
            <input
              type="text"
              className="input"
              placeholder={t(
                "organization:premium-sponsorship.SEARCH_SPONSORSHIPS",
              )}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="select"
              aria-label={t(
                "organization:premium-sponsorship.FILTER_BY_STATUS",
              )}
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(
                  e.target.value as PremiumSponsorshipStatus | "all",
                )
              }
            >
              <option value="all">
                {t("organization:premium-sponsorship.ALL_STATUSES")}
              </option>
              {PREMIUM_SPONSORSHIP_STATUSES.map((status) => (
                <option key={`status-${status}`} value={status}>
                  {t(PREMIUM_SPONSORSHIP_STATUS_LABEL_KEYS[status])}
                </option>
              ))}
            </select>
          </div>

          <PremiumSponsorshipsTable
            organization={organization}
            sponsorships={sortedSponsorships}
            sort={sort}
            onSort={toggleSort}
          />
        </div>
      </div>

      {isInviteDialogOpen && (
        <InvitePremiumSponsorshipsDialog
          onClose={() => setInviteDialogOpen(false)}
          organization={organization}
          sponsorships={sponsorships}
        />
      )}
    </div>
  );
};

export default PremiumSponsorshipPage;
